import config from "@Config/index";
import axios from "axios";
import {
  getInternalHrefPath,
  unwrapAnchors,
} from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/stripBlogLinks";

/**
 * SEO (PLG-3406): keep crawlers off search pages whose region `meta.meta_robots` is noindex.
 * Gated by `config.HIDE_NOINDEX_LINKS`; every step fails open (a failure strips nothing).
 */

// optional language prefix (xx or xx-YY), then /places/search/<slug> as a whole segment.
const SEARCH_PATH = /^(?:\/[a-z]{2}(?:-[a-zA-Z]{2})?)?\/places\/search\/([^/?#]+)\/?(?:[?#]|$)/i;

// Slugs per regions API request, keeping the query string well under URL length limits.
const CHECK_CHUNK_SIZE = 100;

const getSearchSlugFromHref = (rawHref: string): string | null =>
  getInternalHrefPath(rawHref)?.match(SEARCH_PATH)?.[1] || null;

/** Unwrap anchors pointing to noindexed search pages, keeping their visible text. */
export function stripNoindexLinks<T>(html: T, noindexSlugs: Set<string>): T {
  if (!config.HIDE_NOINDEX_LINKS || noindexSlugs.size === 0) return html;
  return unwrapAnchors(html, (href) => noindexSlugs.has(getSearchSlugFromHref(href) || ""));
}

/** Search-page slugs referenced in page data: content links and `canonical_name` fields. */
export function collectSearchSlugs(...sources: unknown[]): string[] {
  const slugs = new Set<string>();
  sources.forEach((source) => {
    let text = "";
    try {
      text = source ? JSON.stringify(source) : "";
    } catch {
      return;
    }
    [/\/places\/search\/([a-z0-9_-]+)/gi, /"canonical_name":"([a-z0-9_-]+)"/gi].forEach(
      (pattern) => {
        let match = pattern.exec(text);
        while (match) {
          slugs.add(match[1]);
          match = pattern.exec(text);
        }
      },
    );
  });
  return Array.from(slugs);
}

/** Which of `slugs` are noindexed; [] when the flag is off or the request fails. */
export async function fetchNoindexSlugs(slugs: string[]): Promise<string[]> {
  if (!config.HIDE_NOINDEX_LINKS || slugs.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < slugs.length; i += CHECK_CHUNK_SIZE) {
    chunks.push(slugs.slice(i, i + CHECK_CHUNK_SIZE));
  }
  try {
    const responses = await Promise.all(
      chunks.map((chunk) =>
        axios.get(`${config.API_URL}/api/v0/regions`, {
          params: {
            canonical_names: chunk.join(","),
            meta_robots_noindex: true,
            only: "canonical_name",
            p: 1,
            limit: chunk.length,
            use_java_api: true,
          },
          ...config.apiParams,
        }),
      ),
    );
    return responses.flatMap(({ data }) =>
      (data?.data?.result || []).map((region: { canonical_name: string }) => region.canonical_name),
    );
  } catch (error) {
    if (typeof window !== "undefined" && typeof window.Sentry !== "undefined") {
      window.Sentry.captureException(`Noindex slug check failed -> ${error}`);
    } else {
      console.error("[noindexLinks] Noindex slug check failed", error);
    }
    return [];
  }
}

/** `items` without the ones whose `canonical_name` is in `noindexSlugs`. */
export function withoutNoindexed<T extends { canonical_name?: string }>(
  items: T[],
  noindexSlugs: string[],
): T[] {
  if (!config.HIDE_NOINDEX_LINKS || noindexSlugs.length === 0) return items;
  return items.filter((item) => !noindexSlugs.includes(item?.canonical_name || ""));
}

/** List-request params that make the regions API leave noindexed regions out. */
export const excludeNoindexParams = (): { meta_robots_noindex?: boolean } =>
  config.HIDE_NOINDEX_LINKS ? { meta_robots_noindex: false } : {};
